import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { GenericList } from './models/JobSharedInterfaces';
import { GetQuery, PostQuery } from './Shared';

interface TruckCheck {
    id: number;
    odometerReading: number;
    oilLevel: number;
    oilLevelOther: string;
    wheelTyre: number;
    wheelTyreOther: string;
    lights: number;
    lightsOther: string;
    windows: number;
    windowsOther: string;
    safety: number;
    safetyOther: string;
    steeringSuspension: number;
    steeringSuspensionOther: string;
    brakes: number;
    brakesOther: string;
    exhaustSystem: number;
    exhaustSystemOther: string;
    cabinTidy: number;
    cabinTidyOther: string;
    looseObjects: number;
    looseObjectsOther: string;
    loadedComponents: number;
    loadedComponentsOther: string;
    loadEven: number;
    loadEvenOther: string;
    loadLevel: number;
    loadLevelOther: string;
    bindersStrapsGood: number;
    bindersStrapsGoodOther: string;
    loadsTiedDown: number;
    loadsTiedDownOther: string;
    notes: string;
    checkCompletedById: number;
}


const TruckChecklistData = () => {
    const { truckId } = useParams();
    let navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [vehicles, setVehicles] = useState<GenericList[]>([]);
    const [currentTruckId, setCurrentTruckId] = useState(-1);
    const [truckName, setTruckName] = useState("");
    const [maintCheck, setMaintCheck] = useState<GenericList[]>([]);
    const [team, setTeam] = useState<GenericList[]>([]);
    const initTruckCheck: TruckCheck = {
        id: 0,
        odometerReading: -1,
        oilLevel: -1,
        oilLevelOther: "",
        wheelTyre: -1,
        wheelTyreOther: "",
        lights: -1,
        lightsOther: "",
        windows: -1,
        windowsOther: "",
        safety: -1,
        safetyOther: "",
        steeringSuspension: -1,
        steeringSuspensionOther: "",
        brakes: -1,
        brakesOther: "",
        exhaustSystem: -1,
        exhaustSystemOther: "",
        cabinTidy: -1,
        cabinTidyOther: "",
        looseObjects: -1,
        looseObjectsOther: "",
        loadedComponents: -1,
        loadedComponentsOther: "",
        loadEven: -1,
        loadEvenOther: "",
        loadLevel: -1,
        loadLevelOther: "",
        bindersStrapsGood: -1,
        bindersStrapsGoodOther: "",
        loadsTiedDown: -1,
        loadsTiedDownOther: "",
        notes: "",
        checkCompletedById: -1
    }
    const [truckCheck, setTruckCheck] = React.useState<TruckCheck>(initTruckCheck);

    useEffect(() => {
        window.scrollTo(0, 0);
        setCurrentTruckId(Number(truckId));
        //getData();
    }, []);

    //get data when truck changes
    useEffect(() => {
        window.scrollTo(0, 0);
        if (currentTruckId > 0) {
            getData();
        }
    }, [currentTruckId]);

    const getData = async () => {
        //var checkTruckId = currentTruckId <= 0 ? truckId : currentTruckId;   //cater for current truck not being set yet
        const apiUrl = "GetTruckCheckData?truckId=" + currentTruckId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setMaintCheck(result.data.maintCheck);
            setTruckCheck(result.data.truckCheck);
            setVehicles(result.data.vehicles);
            setTeam(result.data.team);
            //setCurrentTruckId(result.data.truckId);
            setTruckName(result.data.truckName);
            setLoading(false);
        };
    }

    const back = (e: any) => {
        e.preventDefault();
        navigate("/driverjobs");
    }

    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errorList = errors;
        if (e.target.value) {
            delete errorList[e.target.name];
            var notesfield = e.target.name + "Other";
            delete errorList[notesfield];
            setErrors(errorList);
        }

        setTruckCheck(prevState => ({ ...prevState, [e.target.name]: e.target.value * 1 }));
    }

    const handleChangeText = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errorList = errors;
        if (e.target.value) {
            delete errorList[e.target.name];
            setErrors(errorList);
        }

        setTruckCheck(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleTruckChange = (e: any) => {
        //get check list for new truck
        setCurrentTruckId(e.target.value * 1);
    }

    const validate = () => {
        let fields = truckCheck;
        let errorList: any = {};
        let formIsValid = true;

        if (fields.odometerReading <= 0) {
            formIsValid = false;
            errorList["odometerReading"] = "Odometer Reading required";
        }

        if (!fields.checkCompletedById || fields.checkCompletedById <= 0) {
            formIsValid = false;
            errorList["checkCompletedById"] = "Person Completing Check is required";
        }

        if (!fields.oilLevel || fields.oilLevel < 0) {
            formIsValid = false;
            errorList["oilLevel"] = "Oil Level Check is required";
        }

        if (!fields.oilLevelOther && (fields.oilLevel === 2 || fields.oilLevel === 3)) {
            formIsValid = false;
            errorList["oilLevelOther"] = "Oil Level Check notes are required";
        }

        if (!fields.wheelTyre || fields.wheelTyre < 0) {
            formIsValid = false;
            errorList["wheelTyre"] = "Wheel Check is required";
        }

        if (!fields.wheelTyreOther && (fields.wheelTyre === 2 || fields.wheelTyre === 3)) {
            formIsValid = false;
            errorList["wheelTyreOther"] = "Wheel Check notes are required";
        }

        if (!fields.lights || fields.lights < 0) {
            formIsValid = false;
            errorList["lights"] = "Light Check is required";
        }

        if (!fields.lightsOther && (fields.lights === 2 || fields.lights === 3)) {
            formIsValid = false;
            errorList["lightsOther"] = "Light Check notes are required";
        }

        if (!fields.windows || fields.windows < 0) {
            formIsValid = false;
            errorList["windows"] = "Window Check is required";
        }

        if (!fields.windowsOther && (fields.windows === 2 || fields.windows === 3)) {
            formIsValid = false;
            errorList["windowsOther"] = "Window Check notes are required";
        }

        if (!fields.safety || fields.safety < 0) {
            formIsValid = false;
            errorList["safety"] = "Safety Check is required";
        }

        if (!fields.safetyOther && (fields.safety === 2 || fields.safety === 3)) {
            formIsValid = false;
            errorList["safetyOther"] = "Safety Check notes are required";
        }

        if (!fields.steeringSuspension || fields.steeringSuspension < 0) {
            formIsValid = false;
            errorList["steeringSuspension"] = "Steering Suspension Check is required";
        }

        if (!fields.steeringSuspensionOther && (fields.steeringSuspension === 2 || fields.steeringSuspension === 3)) {
            formIsValid = false;
            errorList["steeringSuspensionOther"] = "Steering Suspension Check notes are required";
        }

        if (!fields.brakes || fields.brakes < 0) {
            formIsValid = false;
            errorList["brakes"] = "Brake Check is required";
        }

        if (!fields.brakesOther && (fields.brakes === 2 || fields.brakes === 3)) {
            formIsValid = false;
            errorList["brakesOther"] = "Brake Check notes are required";
        }

        if (!fields.exhaustSystem || fields.exhaustSystem < 0) {
            formIsValid = false;
            errorList["exhaustSystem"] = "Exhaust Check is required";
        }

        if (!fields.exhaustSystemOther && (fields.exhaustSystem === 2 || fields.exhaustSystem === 3)) {
            formIsValid = false;
            errorList["exhaustSystemOther"] = "Exhaust Check notes are required";
        }

        if (!fields.cabinTidy || fields.cabinTidy < 0) {
            formIsValid = false;
            errorList["cabinTidy"] = "Cabin Check is required";
        }

        if (!fields.cabinTidyOther && (fields.cabinTidy === 2 || fields.cabinTidy === 3)) {
            formIsValid = false;
            errorList["cabinTidyOther"] = "Cabin Check notes are required";
        }

        if (!fields.looseObjects || fields.looseObjects < 0) {
            formIsValid = false;
            errorList["looseObjects"] = "Loose Objects Check is required";
        }

        if (!fields.looseObjectsOther && (fields.looseObjects === 2 || fields.looseObjects === 3)) {
            formIsValid = false;
            errorList["looseObjectsOther"] = "Loose Objects Check notes are required";
        }

        if (!fields.loadedComponents || fields.loadedComponents < 0) {
            formIsValid = false;
            errors["loadedComponents"] = "Loaded Components Check is required";
        }

        if (!fields.loadedComponentsOther && (fields.loadedComponents === 2 || fields.loadedComponents === 3)) {
            formIsValid = false;
            errorList["loadedComponentsOther"] = "Loaded Components Check notes are required";
        }

        if (!fields.loadEven || fields.loadEven < 0) {
            formIsValid = false;
            errorList["loadEven"] = "Load Even Check is required";
        }

        if (!fields.loadEvenOther && (fields.loadEven === 2 || fields.loadEven === 3)) {
            formIsValid = false;
            errorList["loadEvenOther"] = "Load Even Check notes are required";
        }

        if (!fields.loadLevel || fields.loadLevel < 0) {
            formIsValid = false;
            errorList["loadLevel"] = "Load Level Check is required";
        }

        if (!fields.loadLevelOther && (fields.loadLevel === 2 || fields.loadLevel === 3)) {
            formIsValid = false;
            errorList["loadLevelOther"] = "Load Level Check notes are required";
        }

        if (!fields.bindersStrapsGood || fields.bindersStrapsGood < 0) {
            formIsValid = false;
            errorList["bindersStrapsGood"] = "Binders / Straps Check is required";
        }

        if (!fields.bindersStrapsGoodOther && (fields.bindersStrapsGood === 2 || fields.bindersStrapsGood === 3)) {
            formIsValid = false;
            errorList["bindersStrapsGoodOther"] = "Binders / Straps Check notes are required";
        }

        if (!fields.loadsTiedDown || fields.loadsTiedDown < 0) {
            formIsValid = false;
            errorList["loadsTiedDown"] = "Loads Tied Down Check is required";
        }

        if (!fields.loadsTiedDownOther && (fields.loadsTiedDown === 2 || fields.loadsTiedDown === 3)) {
            formIsValid = false;
            errorList["loadsTiedDownOther"] = "Loads Tied Down Check notes are required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveTruckCheck();
        } else {
            toast.error("Fix validation errors on screen before going Next");
        }
    }

    const saveTruckCheck = async () => {
        var saveTruck = {
            truckId: truckId,
            truckName: truckName,
            truckCheck: truckCheck
        };

        var jsonItem = JSON.stringify(saveTruck);

        const apiUrl = "SaveTruckCheck";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Truck Checklist has been saved");
                navigate("/driverjobs");
            }
        };
    }

    var truckCount = vehicles.length;
    const renderDetails = (
        <div>
            <div className="mobileFont marginTop10 marginBottom10">
                <div className="makeitflexspacebetween marginBottom10">
                    <h2 className="mobile-margin">Truck Check</h2>
                    <a className="mobile-icon-grey" href="#" onClick={(e) => back(e)}>
                        <span className="fas fa-arrow-alt-circle-left mobileMenuSize alignIconCenter"></span>
                    </a>
                </div>

                <span className="hidden">Truck Id: {truckCheck.id}</span>
                <label className="input-group makeitflexspacebetween truckcheck-background" htmlFor="truckFilterId">
                    <span className="label-mobile--inline-sm margin5">Truck</span>
                    <select className="select margin5" id="truckId" name="truckId" value={truckId} onChange={(e) => handleTruckChange(e)} disabled={truckCount <= 1}>
                        <option hidden defaultValue="-1"></option>
                        {vehicles.map(truck =>
                            <option key={truck.id} value={truck.id}>{truck.name}</option>
                        )};
                    </select>
                </label>

                <div className={Number(truckId) <= 0 && vehicles.length === 0 ? "" : "hidden"}>
                    <span className="makeitred">There are no jobs allocated to you for today!</span>
                </div>
                <div className={Number(truckId) <= 0 ? "hidden" : ""}>
                    <label className="input-group makeitflexspacebetween truckcheck-background" htmlFor="odometerReading">
                        <span className="label-mobile--inline-sm margin5">Odometer Reading</span>
                        <input type="number" min="1" step="1" className="input truckcheck-odo--size margin5" id="odometerReading" name="odometerReading" value={truckCheck.odometerReading} onChange={(e) => handleChangeText(e)} />
                    </label>
                    <span className={errors["odometerReading"] ? "label errors" : "hidden"}>{errors["odometerReading"]}</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background" htmlFor="checkCompletedById">
                        <span className="label-mobile--inline-md margin5">Person Completing Checks</span>
                        <div className="marginRight10">
                            {team.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" id={item.name} className="marginRight10" name="checkCompletedById" value={item.id} checked={truckCheck.checkCompletedById === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["checkCompletedById"] ? "label errors" : "hidden"}>{errors["checkCompletedById"]}</span>
                    <hr></hr>

                    <h4>Maintenance Checks</h4>
                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="oilLevel">
                        <span className="label-mobile--inline-sm margin5">How are the Oil/Coolant Levels - No Leaks?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="oilLevel" value={item.id} checked={truckCheck.oilLevel === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["oilLevel"] ? "label errors" : "hidden"}>{errors["oilLevel"]}</span>
                    <label className={truckCheck.oilLevel === 2 || truckCheck.oilLevel === 3 ? "input-group truckcheck-background" : "hidden"} htmlFor="oilLevelOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="oilLevelOther" name="oilLevelOther" value={truckCheck.oilLevelOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["oilLevelOther"] ? "label errors" : "hidden"}>{errors["oilLevelOther"]}</span>
                    <span className={truckCheck.oilLevel === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="wheelTyre">
                        <span className="label-mobile--inline-sm margin5">How are the wheels & tyres (pressure & tread)?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="wheelTyre" value={item.id} checked={truckCheck.wheelTyre === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["wheelTyre"] ? "label errors" : "hidden"}>{errors["wheelTyre"]}</span>

                    <label className={truckCheck.wheelTyre === 2 || truckCheck.wheelTyre === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="wheelTyreOther">
                        <span className="label-mobile--inline-sm">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="wheelTyreOther" name="wheelTyreOther" value={truckCheck.wheelTyreOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["wheelTyreOther"] ? "label errors" : "hidden"}>{errors["wheelTyreOther"]}</span>
                    <span className={truckCheck.wheelTyre === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="lights">
                        <span className="label-mobile--inline-sm margin5">Check the operation of Lights, Reverse Beeper</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="lights" value={item.id} checked={truckCheck.lights === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["lights"] ? "label errors" : "hidden"}>{errors["lights"]}</span>

                    <label className={truckCheck.lights === 2 || truckCheck.lights === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="lightsOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="lightsOther" name="lightsOther" value={truckCheck.lightsOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["lightsOther"] ? "label errors" : "hidden"}>{errors["lightsOther"]}</span>
                    <span className={truckCheck.lights === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="windows">
                        <span className="label-mobile--inline-sm margin5">Check the condition of Windscreen, windows, wipers and mirrors</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="windows" value={item.id} checked={truckCheck.windows === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["windows"] ? "label errors" : "hidden"}>{errors["windows"]}</span>

                    <label className={truckCheck.windows === 2 || truckCheck.windows === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="windowsOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="windowsOther" name="windowsOther" value={truckCheck.windowsOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["windowsOther"] ? "label errors" : "hidden"}>{errors["windowsOther"]}</span>
                    <span className={truckCheck.windows === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="safety">
                        <span className="label-mobile--inline-sm margin5">Is the seat belt & safety items operational?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="safety" value={item.id} checked={truckCheck.safety === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["safety"] ? "label errors" : "hidden"}>{errors["safety"]}</span>

                    <label className={truckCheck.safety === 2 || truckCheck.safety === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="safetyOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="safetyOther" name="safetyOther" value={truckCheck.safetyOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["safetyOther"] ? "label errors" : "hidden"}>{errors["safetyOther"]}</span>
                    <span className={truckCheck.safety === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="steeringSuspension">
                        <span className="label-mobile--inline-sm margin5">Is the steering & suspension visually OK?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="steeringSuspension" value={item.id} checked={truckCheck.steeringSuspension === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["steeringSuspension"] ? "label errors" : "hidden"}>{errors["steeringSuspension"]}</span>

                    <label className={truckCheck.steeringSuspension === 2 || truckCheck.steeringSuspension === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="steeringSuspensionOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="steeringSuspensionOther" name="steeringSuspensionOther" value={truckCheck.steeringSuspensionOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["steeringSuspensionOther"] ? "label errors" : "hidden"}>{errors["steeringSuspensionOther"]}</span>
                    <span className={truckCheck.steeringSuspension === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="brakes">
                        <span className="label-mobile--inline-sm margin5">Are the Brakes operational?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="brakes" value={item.id} checked={truckCheck.brakes === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["brakes"] ? "label errors" : "hidden"}>{errors["brakes"]}</span>

                    <label className={truckCheck.brakes === 2 || truckCheck.brakes === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="brakesOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="brakesOther" name="brakesOther" value={truckCheck.brakesOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["brakesOther"] ? "label errors" : "hidden"}>{errors["brakesOther"]}</span>
                    <span className={truckCheck.brakes === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="exhaustSystem">
                        <span className="label-mobile--inline-sm margin5">Is the exhaust system visually & audibly OK?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="exhaustSystem" value={item.id} checked={truckCheck.exhaustSystem === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["exhaustSystem"] ? "label errors" : "hidden"}>{errors["exhaustSystem"]}</span>

                    <label className={truckCheck.exhaustSystem === 2 || truckCheck.exhaustSystem === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="exhaustSystemOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="exhaustSystemOther" name="exhaustSystemOther" value={truckCheck.exhaustSystemOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["exhaustSystemOther"] ? "label errors" : "hidden"}>{errors["exhaustSystemOther"]}</span>
                    <span className={truckCheck.exhaustSystem === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="cabinTidy">
                        <span className="label-mobile--inline-sm margin5">Is the Cabin, body clean & tidy (no defects)?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="cabinTidy" value={item.id} checked={truckCheck.cabinTidy === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["cabinTidy"] ? "label errors" : "hidden"}>{errors["cabinTidy"]}</span>

                    <label className={truckCheck.cabinTidy === 2 || truckCheck.cabinTidy === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="cabinTidyOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="cabinTidyOther" name="cabinTidyOther" value={truckCheck.cabinTidyOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["cabinTidyOther"] ? "label errors" : "hidden"}>{errors["cabinTidyOther"]}</span>
                    <span className={truckCheck.cabinTidy === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="looseObjects">
                        <span className="label-mobile--inline-sm margin5">Are there any loose objects on the body or tray?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="looseObjects" value={item.id} checked={truckCheck.looseObjects === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["looseObjects"] ? "label errors" : "hidden"}>{errors["looseObjects"]}</span>

                    <label className={truckCheck.looseObjects === 2 || truckCheck.looseObjects === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="looseObjectsOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="looseObjectsOther" name="looseObjectsOther" value={truckCheck.looseObjectsOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["looseObjectsOther"] ? "label errors" : "hidden"}>{errors["looseObjectsOther"]}</span>
                    <span className={truckCheck.looseObjects === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>
                    <hr></hr>

                    <h4>Vehicle Loading</h4>
                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="loadedComponents">
                        <span className="label-mobile--inline-sm margin5">Are all loaded components accessible?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="loadedComponents" value={item.id} checked={truckCheck.loadedComponents === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["loadedComponents"] ? "label errors" : "hidden"}>{errors["loadedComponents"]}</span>

                    <label className={truckCheck.loadedComponents === 2 || truckCheck.loadedComponents === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="loadedComponentsOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="loadedComponentsOther" name="loadedComponentsOther" value={truckCheck.loadedComponentsOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["loadedComponentsOther"] ? "label errors" : "hidden"}>{errors["loadedComponentsOther"]}</span>
                    <span className={truckCheck.loadedComponents === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="loadEven">
                        <span className="label-mobile--inline-sm margin5">Is the load evenly dispersed?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="loadEven" value={item.id} checked={truckCheck.loadEven === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["loadEven"] ? "label errors" : "hidden"}>{errors["loadEven"]}</span>

                    <label className={truckCheck.loadEven === 2 || truckCheck.loadEven === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="loadEvenOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="loadEvenOther" name="loadEvenOther" value={truckCheck.loadEvenOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["loadEvenOther"] ? "label errors" : "hidden"}>{errors["loadEvenOther"]}</span>
                    <span className={truckCheck.loadEven === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="loadLevel">
                        <span className="label-mobile--inline-sm margin5">Is the loaded truck level - not tilting to one side?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="loadLevel" value={item.id} checked={truckCheck.loadLevel === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["loadLevel"] ? "label errors" : "hidden"}>{errors["loadLevel"]}</span>

                    <label className={truckCheck.loadLevel === 2 || truckCheck.loadLevel === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="loadLevelOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="loadLevelOther" name="loadLevelOther" value={truckCheck.loadLevelOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["loadLevelOther"] ? "label errors" : "hidden"}>{errors["loadLevelOther"]}</span>
                    <span className={truckCheck.loadLevel === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="bindersStrapsGood">
                        <span className="label-mobile--inline-sm margin5">Are the binders & straps in good order - no cuts?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="bindersStrapsGood" value={item.id} checked={truckCheck.bindersStrapsGood === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["bindersStrapsGood"] ? "label errors" : "hidden"}>{errors["bindersStrapsGood"]}</span>

                    <label className={truckCheck.bindersStrapsGood === 2 || truckCheck.bindersStrapsGood === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="bindersStrapsGoodOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="bindersStrapsGoodOther" name="bindersStrapsGoodOther" value={truckCheck.bindersStrapsGoodOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["bindersStrapsGoodOther"] ? "label errors" : "hidden"}>{errors["bindersStrapsGoodOther"]}</span>
                    <span className={truckCheck.bindersStrapsGood === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>

                    <label className="input-group makeitflexspacebetween truckcheck-background jobcheck-border" htmlFor="loadsTiedDown">
                        <span className="label-mobile--inline-sm margin5">Are all loads tied down securely?</span>
                        <div className="marginRight10">
                            {maintCheck.map(item =>
                                <div className="input-group alignitemscentre" key={item.id}>
                                    <input type="radio" className="marginRight10" id={item.name} name="loadsTiedDown" value={item.id} checked={truckCheck.loadsTiedDown === item.id} onChange={(e) => handleChange(e)}></input>
                                    <span className="makeitflex makeitcentervertical marginRight10">{item.name}</span>
                                </div>)
                            }
                        </div>
                    </label>
                    <span className={errors["loadsTiedDown"] ? "label errors" : "hidden"}>{errors["loadsTiedDown"]}</span>

                    <label className={truckCheck.loadsTiedDown === 2 || truckCheck.loadsTiedDown === 3 ? "input-group makeitflexspacebetween truckcheck-background" : "hidden"} htmlFor="loadsTiedDownOther">
                        <span className="label-mobile--inline-sm margin5">Notes</span>
                        <input className="input--mobile margin5" maxLength={200} type="text" id="loadsTiedDownOther" name="loadsTiedDownOther" value={truckCheck.loadsTiedDownOther} onChange={(e) => handleChangeText(e)}></input>
                    </label>
                    <span className={errors["loadsTiedDownOther"] ? "label errors" : "hidden"}>{errors["loadsTiedDownOther"]}</span>
                    <span className={truckCheck.loadsTiedDown === 3 ? "label errors" : "hidden"}>Please call your supervisor for instructions.</span>
                    <hr></hr>

                    <label className="input-group" htmlFor="notes">
                        <span className="label-mobile">Additional Notes</span>
                        <textarea className="input marginLeft0 job__noteSize label-mobile" id="notes" name="notes" value={truckCheck.notes} onChange={(e) => handleChangeText(e)}></textarea>
                    </label>

                    <div className="defaultbutton__container--right marginTop10">
                        <a className="mobile-icon-green" href="#" onClick={(e) => save(e)}>
                            <span className="fas fa-circle-check mobileMenuSize alignIconCenter"></span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return <div>
        {contents}
    </div>;
}

export default TruckChecklistData;